/**
 * A driver pressed the alarm.
 *
 * Red, across the top of every view, and there until somebody clears it: the one thing on
 * the screen that must not depend on which view a dispatcher has open or whether they were
 * looking. It carries what the next thirty seconds need — the name, the car, the number to
 * ring and the ride — and a button that puts the car on the map.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@taxi/api-client';

import { useDispatcherHub } from '../realtime/useDispatcherHub';
import { useDispatch } from '../shell/dispatchContext';
import { useTenantFormats } from '../shell/formats';
import { Banner, Button } from '../ui';
import { alarmsApi, type Alarm } from './api';

export function AlarmBanner() {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const navigate = useNavigate();
  const { showDriver } = useDispatch();

  const [alarms, setAlarms] = useState<Alarm[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setAlarms(await alarmsApi.open());
    } catch (failure) {
      // A firm with the alarm off is refused; nothing to show and nothing to say.
      if (failure instanceof ApiError && failure.problem.code === 'alarm.disabled') setAlarms([]);
    }
  }, []);

  // An async fetch rather than a call to refresh(), so nothing sets state on the render
  // that scheduled this.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const open = await alarmsApi.open();

        if (!cancelled) setAlarms(open);
      } catch {
        // As above.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useDispatcherHub({
    // Patched from the event rather than refetched: an alarm is the one message worth
    // showing in the same instant it arrives, and the list is refetched on reconnect anyway.
    onDriverAlarmChanged: (change) => {
      setAlarms((current) => {
        const rest = current.filter((alarm) => alarm.id !== change.alarmId);

        if (change.clearedAt !== null) return rest;

        return [
          ...rest,
          {
            id: change.alarmId,
            driverId: change.driverId,
            driverName: change.driverName,
            callSign: change.callSign,
            phoneNumber: change.phoneNumber,
            vehiclePlate: change.vehiclePlate,
            longitude: change.longitude,
            latitude: change.latitude,
            orderId: change.orderId,
            orderReference: change.orderReference,
            raisedAt: change.raisedAt,
            clearedAt: null,
          },
        ].sort((a, b) => a.raisedAt.localeCompare(b.raisedAt));
      });
    },
    onReconnected: () => void refresh(),
  });

  async function clear(alarm: Alarm) {
    setBusy(alarm.id);

    try {
      await alarmsApi.clear(alarm.id);
      setAlarms((current) => current.filter((open) => open.id !== alarm.id));
    } catch {
      // Somebody else cleared it a moment ago, most likely. The hub says so.
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  if (alarms.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 px-3 pt-3">
      {alarms.map((alarm) => (
        <Banner
          key={alarm.id}
          tone="danger"
          actions={
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  showDriver(alarm.driverId);
                  void navigate('/map');
                }}
              >
                {t('alarms.showOnMap')}
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={busy === alarm.id}
                onClick={() => void clear(alarm)}
              >
                {t('alarms.clear')}
              </Button>
            </>
          }
        >
          <strong className="font-semibold">
            {t('alarms.raised', {
              driver: alarm.callSign === null ? alarm.driverName : `${alarm.driverName} (${alarm.callSign})`,
              time: formats.time(alarm.raisedAt),
            })}
          </strong>
          {' · '}
          {alarm.vehiclePlate !== null && <span className="font-mono">{alarm.vehiclePlate} · </span>}
          {alarm.phoneNumber !== null ? (
            <a href={`tel:${alarm.phoneNumber}`} className="underline">
              {alarm.phoneNumber}
            </a>
          ) : (
            t('alarms.noPhone')
          )}
          {alarm.orderReference !== null && (
            <>
              {' · '}
              {t('alarms.onOrder', { reference: alarm.orderReference })}
            </>
          )}
        </Banner>
      ))}
    </div>
  );
}
